import * as React from 'react';
import { EMBEDDED_CONFLUENCE_SHIM_URL, PORTAL_PARENT_PRODUCT } from '@helpcenter/constants';
import {
    isKbArticlesSameTabRedirectionEnabled,
    isCrossSiteConnieConnectionEnabled,
    isGdayFeaturesForKbArticlesEnabled,
    isThemeStatePassed,
} from '@helpcenter/feature-flags';
import { di } from 'react-magnetic-di';
import { useQueryParam } from 'react-resource-router';
import type { CreateUIAnalyticsEvent, WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useThemeObserver } from '@atlaskit/tokens';
import { OPERATIONAL_EVENT_TYPE, TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { ViewPage } from '@atlassian/embedded-confluence';
import type { ExperienceEvent } from '@atlassian/experience-tracker';
import { ExperienceTracker, ExperienceTrackerContext } from '@atlassian/experience-tracker';
import {
    getSlugForAdvancedHCFromArticleView,
    isAdvancedHelpCenter,
} from '@atlassian/help-center-common-util/advanced-help-center/advanced-help-center';
import { history } from '@atlassian/help-center-common-util/history';
import { hash, href } from '@atlassian/help-center-common-util/location';

const { useEffect, useState } = React;
export interface ConfluenceEmbeddedViewProps {
    articleId: string;
    hostname?: string;
    spaceKey: string | undefined;
    projectId: number | undefined;
    projectAri?: string;
    onIFrameLoaded?: () => void;
    isAnonymousAccount: boolean;
    kbLinkDomainURLs: string[];
    isCrossSiteArticle?: boolean;
    linkedSpaces?: string[];
}

export interface Modifiers {
    target: '_self' | '_blank';
    routeName: 'EDIT_PAGE_EMBED' | 'VIEW_PAGE' | undefined;
    contentId: string | undefined;
    spaceKey: string | undefined;
}
export interface NavigationPolicy {
    navigate?: (
        url: string,
        modifiers: Modifiers,
        defaultNavigate: (url1: string, modifiers1: Modifiers) => void
    ) => void;
    shimUrl?: string;
    domainNamesToReplace?: string[];
    spaceKeyFilter?: string[];
}

export const experienceTrackerCallback = (
    payload: ExperienceEvent,
    articleId: string,
    utmSource: string | null,
    issueId: string | null,
    source: string | undefined,
    isCrossSiteArticle: boolean,
    onIFrameLoaded?: () => void,
    createAnalyticsEvent?: CreateUIAnalyticsEvent
) => {
    const { action, name, id } = payload;
    switch (action) {
        case 'taskSuccess':
            if (name === 'view-content' && id === articleId) {
                createAnalyticsEvent &&
                    createAnalyticsEvent({
                        utmSource,
                        issueId,
                        source,
                        isCrossSiteArticle,
                        articleId,
                        action: 'portalEpView success',
                        analyticsType: TRACK_EVENT_TYPE,
                        actionSubject: 'KBArticle',
                    }).fire();
                onIFrameLoaded && onIFrameLoaded();
            }
            break;
        case 'taskFail':
            if (name === 'view-content' && id === articleId) {
                createAnalyticsEvent &&
                    createAnalyticsEvent({
                        utmSource,
                        issueId,
                        isCrossSiteArticle,
                        source,
                        action: 'portalEpView failure',
                        analyticsType: OPERATIONAL_EVENT_TYPE,
                    }).fire();
            }
            break;
        default:
            break;
    }
};

export const ConfluenceArticleViewPage = ({
    articleId,
    hostname,
    spaceKey,
    projectId,
    projectAri,
    onIFrameLoaded,
    createAnalyticsEvent,
    kbLinkDomainURLs,
    isCrossSiteArticle,
    linkedSpaces,
}: ConfluenceEmbeddedViewProps & WithAnalyticsEventsProps) => {
    di(ViewPage);
    const [experienceTracker] = useState(new ExperienceTracker());
    const themeState = useThemeObserver();
    const [source] = useQueryParam('source');
    // eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-in-react-fc
    const currentUrl = new URL(window.location.href);

    const utmSource = currentUrl.searchParams.get('utm_source');

    const issueId = currentUrl.searchParams.get('issueId');

    useEffect(() => {
        return experienceTracker.subscribe((payload: ExperienceEvent) =>
            experienceTrackerCallback(
                payload,
                articleId,
                utmSource,
                issueId,
                source,
                isCrossSiteArticle || false,
                onIFrameLoaded,
                createAnalyticsEvent
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experienceTracker, articleId, source, isCrossSiteArticle]);

    const getParentProductContentContainerId = () => {
        if (isCrossSiteConnieConnectionEnabled()) {
            return projectAri;
        }

        return projectId !== undefined ? String(projectId) : undefined;
    };

    const getShimURLForAdvancedHCLinkedArticle = () => {
        const slug = getSlugForAdvancedHCFromArticleView(href());
        return slug ? `/plugins/servlet/helpcenter/${slug}/confluence/shim/` : undefined;
    };

    const getHash = () => hash() || undefined;

    const navigationPolicy: NavigationPolicy = {
        ...(isKbArticlesSameTabRedirectionEnabled() && {
            navigate: (url, modifiers, defaultNavigate) => {
                if (url.includes('/confluence/shim/')) {
                    const regex = /\/pages\/([^/]+)\/?[^#]*(#.*)?/;
                    /*
                        ([^\/]+) captures one or more of any character that is not a "/" character. This is the page ID.

                        \/? matches zero or one "/" character. This is used to account for URLs where there might not be a trailing slash after the page ID.

                        [^#]* matches zero or more of any character that is not a "#" character. This is used to skip any characters until a "#" character (or the end of the string) is found.

                        (#.*)? captures the '#' symbol followed by zero or more of any character. The '?' after the group makes this group optional, meaning it will still match URLs that don't have a hash.
                    */
                    const match = url.match(regex);

                    if (match) {
                        const pageId = match[1];
                        const articleHash = match[2];
                        history.push(`/article/${pageId}${articleHash || ''}`);
                    } else {
                        defaultNavigate(url, modifiers);
                    }
                } else {
                    defaultNavigate(url, modifiers);
                }
            },
        }),
        shimUrl: isAdvancedHelpCenter() ? getShimURLForAdvancedHCLinkedArticle() : EMBEDDED_CONFLUENCE_SHIM_URL,
        domainNamesToReplace: kbLinkDomainURLs.map((url: string) => url.replace(/^https?:\/\//, '')),
        spaceKeyFilter:
            FeatureGates.checkGate('fetch-all-accesible-linked-spaces-for-user') && !isCrossSiteArticle
                ? linkedSpaces || []
                : undefined,
    };

    return (
        <ExperienceTrackerContext.Provider value={experienceTracker}>
            <ViewPage
                hash={getHash()}
                allowedFeatures={
                    isGdayFeaturesForKbArticlesEnabled()
                        ? ['disable-login-flow', 'disable-share', 'inline-comments', 'page-comments', 'page-reactions']
                        : ['disable-login-flow', 'disable-share']
                }
                hasFooterLogo={false}
                contentId={articleId}
                parentProduct={PORTAL_PARENT_PRODUCT}
                parentProductContentContainerId={getParentProductContentContainerId()}
                spaceKey={spaceKey}
                navigationPolicy={
                    FeatureGates.checkGate('remove_url_shim_in_ep_view_for_cross-site') && isCrossSiteArticle
                        ? undefined
                        : navigationPolicy
                }
                hostname={hostname}
                // @ts-expect-error
                themeState={
                    isThemeStatePassed()
                        ? {
                              ...themeState,
                              typography: themeState.typography,
                          }
                        : undefined
                }
            />
        </ExperienceTrackerContext.Provider>
    );
};

export const ConfluenceEmbeddedView = withAnalyticsEvents()(ConfluenceArticleViewPage);
